'use client'

import { FC, useState, useMemo } from 'react'
import OutsideClickHandler from 'react-outside-click-handler'
import { Separator } from '@/root/modules/shared/SelectTicketPage/components/PassengerForm'
import { ChevronDownIconWhite } from '@/root/ui/shared/icons/ChevronDownIconWhite'
import cn from 'classnames'

type ValueOption = { value: string | number; label: string }
type Separator = { type: 'separator' }
export type Option = ValueOption | Separator

interface FormDropdownProps {
  value: string | number
  options: Option[]
  onChange: (value: string | number) => void
  placeholder?: string
  searchPlaceholder?: string
  searchable?: boolean
  className?: string
  disabled?: boolean
  testId?: string
}

const separatorKeyMap = new WeakMap<Separator, string>()
let separatorKeyCounter = 0

const getSeparatorKey = (separator: Separator): string => {
  if (!separatorKeyMap.has(separator)) {
    separatorKeyMap.set(separator, `separator-${separatorKeyCounter++}`)
  }
  return separatorKeyMap.get(separator)!
}

// --- Type Guards ---
const isSeparator = (option: Option): option is Separator => 'type' in option && option.type === 'separator'
const isValueOption = (option: Option): option is ValueOption => 'value' in option && 'label' in option

export const FormDropdown: FC<FormDropdownProps> = ({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  searchable = false,
  className,
  disabled = false,
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selected = options.find((o): o is ValueOption => isValueOption(o) && o.value === value)

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (o) => isSeparator(o) || (isValueOption(o) && o.label.toLowerCase().includes(search.toLowerCase()))
      ),
    [options, search]
  )

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        setIsOpen(false)
        setSearch('')
      }}
    >
      <div className={cn('relative text-base', className)}>
        <button
          type="button"
          data-testid={testId}
          disabled={disabled}
          onClick={() => {
            if (disabled) return
            setIsOpen((v) => !v)
            setSearch('')
          }}
          className={cn(
            'border-neutral-medium-lighter flex h-9 w-full items-center justify-between gap-2 rounded-lg border bg-white px-2.5 text-base',
            disabled ? 'opacity-50' : 'cursor-pointer'
          )}
        >
          <span
            className={cn(
              'flex-1 truncate text-left',
              selected ? 'text-neutral-darker' : 'text-neutral-medium-lighter'
            )}
          >
            {selected?.label ?? placeholder}
          </span>

          <ChevronDownIconWhite
            width={14}
            height={8}
            color="neutral"
            className={cn(
              'shrink-0 transition-transform duration-150 ease-out',
              isOpen ? '-scale-y-100' : 'scale-y-100'
            )}
          />
        </button>

        {isOpen && !disabled && (
          <div className="border-neutral-medium-lighter absolute z-30 mt-px max-h-64 w-full overflow-auto rounded-lg border bg-white shadow">
            {searchable && (
              <div className="border-neutral-medium-lighter border-b px-2.5 py-2">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  autoFocus
                  className="text-neutral-darker placeholder:text-neutral-medium-lighter w-full border-none text-base outline-none"
                />
              </div>
            )}

            {filteredOptions.map((option) => {
              if (isSeparator(option)) {
                return <Separator key={getSeparatorKey(option)} />
              }

              return (
                <div
                  key={option.value}
                  onClick={() => {
                    if (disabled) return
                    onChange(option.value)
                    setIsOpen(false)
                    setSearch('')
                  }}
                  className="text-primary-base hover:bg-neutral-very-light cursor-pointer px-3 py-0.5"
                >
                  {option.label}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  )
}
